/**
 * Cloud layers at runtime: procedural, animated gaussian-splat clouds drawn with the scan.
 *
 * Each layer is two entities under the scene-content root:
 * - the layer root, which carries the authored transform (position, yaw, and the box size as its
 *   scale): the editor's gizmo, the outliner and keyframes move it;
 * - a child holding a unified gsplat component whose resource is a GSplatContainer filled by
 *   cloudGenerator.ts, with the cloud GSplatFormat (cloudShaders.ts). The child cancels the
 *   root's scale, so the splats are never stretched; the box size reaches the shader as a uniform.
 *
 * Because the clouds are unified splats, the engine writes them into the same work buffer as the
 * scan and sorts them together, so a cloud passing behind a peak is hidden by it, and fog,
 * relighting and the other splat effects apply to clouds like any splat.
 *
 * Animation runs on the GPU, in the copy stage: wind drift (with wrap-around), billowing, the
 * life cycle and the lighting all come from uniforms, so a frame re-copies only the cloud splats.
 * The CPU sorter (WebGL2) sorts by the container's centres; those are refreshed from the same
 * placement formula whenever the clouds have drifted noticeably (WebGPU sorts on the GPU from the
 * work buffer, so it needs none). While no animating layer is on screen nothing is pushed, and
 * on-demand rendering idles.
 *
 * Cloud shadows: layers that cast give the scan's shadow receiving code a column-density map,
 * projected along the sun (cloudShaders.ts CLOUD_SHADOW_*), bound to the relighting materials.
 */
import * as pc from 'playcanvas';
import type { CloudLayerData, EntityAnimationConfig } from '../../types';
import { generateCloudLayer, cloudSplatCenter, type CloudGeometry } from './cloudGenerator';
import {
    cloudGenerationBox,
    cloudGeometryChanged,
    cloudHexToLinear,
    cloudScaleKeyframeExtent,
    cloudHexToSrgb,
    cloudLayerBudget,
    cloudSrgbToHex,
    resolveCloudLook,
    selectCloudTier,
    windVector,
    type CloudLook,
    type CloudQualityInputs,
    type CloudScaleExtent,
    type CloudTier,
} from './cloudParams';
import {
    CLOUD_READ_GLSL,
    CLOUD_READ_WGSL,
    CLOUD_SHADOW_UNIFORMS,
    CLOUD_STREAMS,
    CLOUD_UNIFORMS,
    MAX_CLOUD_SHADOW_LAYERS,
} from './cloudShaders';
import { getCloudShadowPlaceholder } from './cloudShadowPlaceholder';
import { cloudSceneWind, type ResolvedSceneWind } from '../wind/windParams';
import { CloudMeshShadows } from './cloudMeshShadows';
import type { ScanGridBinding } from './scanSky';

/** Seconds after which the layer clock wraps (billowing phases repeat; the jump is invisible). */
const CLOUD_TIME_PERIOD = 3600;
/** Seconds of a form / dissipate cycle when the life cycle is on. */
const CLOUD_LIFE_PERIOD = 150;
/** Gain on the direct (sun) term: calibrated so lit tops land near white in daylight. */
const DIRECT_GAIN = 0.62;
/** Fallback sun when the scene has no directional light: toward-sun direction (PlayCanvas). */
const FALLBACK_SUN = new pc.Vec3(-0.65, 1, 0.5).normalize();

interface Vec3Like {
    x: number;
    y: number;
    z: number;
}

export interface CloudSystemDeps {
    app: pc.AppBase;
    /** Main camera entity. */
    camera: pc.Entity;
    /** Scene-content root the layers are children of. */
    sceneContent: pc.Entity;
    qualityInputs: () => CloudQualityInputs;
    requestFrame: (reason: string) => void;
    /** Splat relighting: on (the scene's lights colour the splats) and its current fade. */
    relighting: () => { enabled: boolean; fade: number };
    toPlayCanvasPosition: (value: unknown) => Vec3Like;
    finiteVec3: (value: unknown, fallback?: Vec3Like) => Vec3Like;
    /** Editor: regenerate after edits settle (ms); the viewer generates at once. */
    regenerateDelayMs?: number;
    /** Roots of what else receives cloud shadows: models that receive shadows, the shadow ground plane. */
    meshReceivers?: () => pc.Entity[];
    /** Cloud shadows on meshes switched on or off (the water's sun glint follows). */
    onMeshShadows?: (on: boolean) => void;
    /** The scene wind now (effects/wind): layers with followSceneWind drift with it. */
    sceneWind?: () => ResolvedSceneWind | null;
}

interface CloudLayerRuntime {
    id: string;
    data: CloudLayerData;
    look: CloudLook;
    /** Keyframed overrides of look fields. */
    animated: Partial<CloudLayerData>;
    root: pc.Entity;
    splat: pc.Entity;
    container: pc.GSplatContainer | null;
    geometry: CloudGeometry | null;
    densityTex: pc.Texture | null;
    shadowTex: pc.Texture | null;
    /** Budget the geometry was generated with, and the inputs, to tell when to regenerate. */
    genKey: string;
    /** Bumped on every generation (new textures): part of the shadow binding key. */
    genVersion: number;
    genMs: number;
    regenAt: number;
    time: number;
    drift: { u: number; v: number };
    /** The drift the cloud shadows on the scan last used (they step coarser while the clouds are off screen). */
    shadowDrift: { u: number; v: number };
    /** World distance drifted since the CPU centres were last refreshed, and when. */
    driftSinceRefresh: number;
    refreshedAt: number;
    centerRefreshes: number;
    /** Uniform values last pushed (component setParameter re-copies the splats, so only on change). */
    pushed: Map<string, Float32Array>;
    onScreen: boolean;
    animating: boolean;
    box: { x: number; y: number; z: number };
    scaleKey: string;
    /** The largest size the timeline gives the box (null: its size isn't keyframed). */
    keyframedScale: CloudScaleExtent | null;
    showBounds: boolean;
    /** The scene wind the look was resolved with ('' = its own wind). */
    sceneWindKey: string;
}

let sharedFormat: { device: pc.GraphicsDevice; format: pc.GSplatFormat } | null = null;

function cloudFormat(device: pc.GraphicsDevice): pc.GSplatFormat {
    if (sharedFormat?.device === device) return sharedFormat.format;
    const PC = pc as unknown as { GSplatFormat: new (d: pc.GraphicsDevice, s: Array<{ name: string; format: number }>, o: { readGLSL: string; readWGSL: string }) => pc.GSplatFormat };
    const format = new PC.GSplatFormat(device, CLOUD_STREAMS.map((name) => ({ name, format: pc.PIXELFORMAT_RGBA32F })), {
        readGLSL: CLOUD_READ_GLSL,
        readWGSL: CLOUD_READ_WGSL,
    });
    sharedFormat = { device, format };
    return format;
}

const _framePlane = new pc.Plane();
/**
 * True when an AABB is (partly) inside the frustum. Tested against the box itself, not its
 * bounding sphere: a cloud layer is a wide, thin slab whose sphere reaches far below it, so a
 * sphere test would count a camera looking at the ground as looking at the clouds. The plane
 * test alone still passes a slab that spans the camera (each plane sees part of it), so the
 * frustum's corners are also tested against the box's faces (both directions of the separating
 * axis test that matter here).
 */
export function boxInFrustum(aabb: pc.BoundingBox, frustum: pc.Frustum, corners?: ArrayLike<pc.Vec3>): boolean {
    const c = aabb.center;
    const h = aabb.halfExtents;
    for (let p = 0; p < 6; p++) {
        // Frustum#planes is gone since PlayCanvas 2.22 (it returns []): read each plane instead
        const { normal: n, distance } = frustum.getPlane(p, _framePlane);
        const r = Math.abs(n.x) * h.x + Math.abs(n.y) * h.y + Math.abs(n.z) * h.z;
        if (n.x * c.x + n.y * c.y + n.z * c.z + distance <= -r) return false;
    }
    if (corners && corners.length === 8) {
        const min = [c.x - h.x, c.y - h.y, c.z - h.z];
        const max = [c.x + h.x, c.y + h.y, c.z + h.z];
        for (let axis = 0; axis < 3; axis++) {
            let below = 0, above = 0;
            for (let k = 0; k < 8; k++) {
                const v = axis === 0 ? corners[k].x : axis === 1 ? corners[k].y : corners[k].z;
                if (v < min[axis]) below++;
                else if (v > max[axis]) above++;
            }
            if (below === 8 || above === 8) return false;
        }
    }
    return true;
}

const _corners = Array.from({ length: 8 }, () => new pc.Vec3());

/**
 * The clouds' sun: the brightest enabled directional light shining down (its +Y, toward the sun,
 * above the horizon). Null when there is none (the clouds then use a default sun high in the sky).
 * Read again every few frames and its direction every frame, so a keyframed sun is followed.
 */
export function pickCloudSun<T extends { enabled: boolean; type: string; intensity: number; entity: { enabled: boolean; up: { y: number } } }>(lights: ReadonlyArray<T>): T | null {
    let best: T | null = null;
    for (const light of lights) {
        if (!light.enabled || !light.entity.enabled || light.type !== 'directional') continue;
        if (light.entity.up.y < 0.02) continue;
        if (!best || light.intensity > best.intensity) best = light;
    }
    return best;
}

/** A difference of two wrapped (0..1) positions, as the shortest signed step. */
function wrapSigned(d: number): number {
    return d - Math.round(d);
}

const _m = new pc.Mat4();
const _v = new pc.Vec3();
const _v2 = new pc.Vec3();
const _q = new pc.Quat();

export class CloudSystem {
    private readonly deps: CloudSystemDeps;
    private readonly layers = new Map<string, CloudLayerRuntime>();
    private tier: CloudTier;
    private readonly onUpdate: (dt: number) => void;
    private readonly onPrerender: () => void;
    private animatingOnScreen = false;
    private sunEntityRef: pc.Entity | null = null;
    private readonly sunDir = new pc.Vec3().copy(FALLBACK_SUN);
    private sunColor: [number, number, number] = [1, 0.97, 0.92];
    private sunScan = 0;
    private shadowPlaceholder: pc.Texture | null = null;
    private readonly shadowKeys = new WeakMap<pc.Material, string>();
    private shadowLayers: CloudLayerRuntime[] = [];
    private destroyed = false;
    private scanGrid: ScanGridBinding | null = null;
    private scanGridPlaceholder: pc.Texture | null = null;
    private animations: ReadonlyArray<EntityAnimationConfig> = [];
    private meshShadows: CloudMeshShadows | null = null;
    private meshShadowKey = '';
    private meshShadowsOn = false;
    /** Every layer hidden (the editor's Splat Edit edits the raw scan); see setSuspended. */
    private suspended = false;

    constructor(deps: CloudSystemDeps) {
        this.deps = deps;
        this.tier = selectCloudTier(deps.qualityInputs());
        this.onUpdate = (dt: number) => this.tick(dt);
        this.onPrerender = () => this.prerender();
        deps.app.on('update', this.onUpdate);
        deps.app.on('prerender', this.onPrerender);
    }

    // ── public API ───────────────────────────────────────────────────────────────────────

    get size(): number {
        return this.layers.size;
    }

    /** Number of cloud layers (a scene with none skips all scan-sky work). */
    get layerCount(): number {
        return this.layers.size;
    }

    get qualityTier(): CloudTier {
        return this.tier;
    }

    has(id: string): boolean {
        return this.layers.has(id);
    }

    entity(id: string): pc.Entity | null {
        return this.layers.get(id)?.root ?? null;
    }

    data(id: string): CloudLayerData | null {
        return this.layers.get(id)?.data ?? null;
    }

    ids(): string[] {
        return [...this.layers.keys()];
    }

    layerOfEntity(entity: pc.Entity): string | null {
        for (const layer of this.layers.values()) if (layer.root === entity) return layer.id;
        return null;
    }

    /**
     * Hide every layer, and layers added meanwhile, without touching their data (each keeps its
     * own enabled state). Splat Edit suspends the clouds: they are splats that would draw over the
     * scan being edited, and the editor works on the raw scan.
     */
    setSuspended(on: boolean): void {
        if (this.suspended === on) return;
        this.suspended = on;
        for (const layer of this.layers.values()) {
            layer.splat.enabled = !on;
            layer.pushed.clear();
        }
        if (on) this.animatingOnScreen = false;
        this.deps.requestFrame('clouds');
    }

    get isSuspended(): boolean {
        return this.suspended;
    }

    /** A layer is drawn: enabled in the scene and the clouds are not suspended. */
    private shown(layer: CloudLayerRuntime): boolean {
        return layer.root.enabled && !this.suspended;
    }

    /** True while an animating layer is on screen (on-demand rendering keeps drawing). */
    isAnimatingOnScreen(): boolean {
        return this.animatingOnScreen;
    }

    /** The directional light the clouds take their sun from (null: the fallback sun). */
    sunEntity(): pc.Entity | null {
        return this.sunEntityRef;
    }

    /** Toward the clouds' sun (world, unit): the scene's sun, or the fallback when it has none. */
    sunDirection(): pc.Vec3 {
        if (!this.sunEntityRef) this.scanSun();
        return this.sunDir;
    }

    /** True when the scan should take cloud shadows (a casting layer exists). */
    wantsShadowReceiving(): boolean {
        for (const layer of this.layers.values()) if (this.castsShadows(layer)) return true;
        return false;
    }

    add(data: CloudLayerData, index = this.layers.size): pc.Entity {
        const id = data.id || `clouds-${Date.now()}-${index}`;
        if (this.layers.has(id)) this.remove(id);
        const root = new pc.Entity(id);
        const splat = new pc.Entity(`${id}-splats`);
        splat.enabled = !this.suspended;
        root.addChild(splat);
        const layer: CloudLayerRuntime = {
            id,
            data: { ...data, id },
            look: resolveCloudLook(data),
            sceneWindKey: '',
            animated: {},
            root,
            splat,
            container: null,
            geometry: null,
            densityTex: null,
            shadowTex: null,
            genKey: '',
            genVersion: 0,
            genMs: 0,
            regenAt: 0,
            time: 0,
            drift: { u: 0, v: 0 },
            shadowDrift: { u: 0, v: 0 },
            driftSinceRefresh: 0,
            refreshedAt: 0,
            centerRefreshes: 0,
            pushed: new Map(),
            onScreen: false,
            animating: false,
            box: { x: 1, y: 1, z: 1 },
            scaleKey: '',
            keyframedScale: cloudScaleKeyframeExtent(this.animations, id),
            showBounds: false,
        };
        this.layers.set(id, layer);
        layer.look = this.resolveLook(layer);
        this.applyTransform(layer, layer.data);
        root.enabled = data.enabled !== false;
        this.deps.sceneContent.addChild(root);
        this.syncScale(layer);
        this.generate(layer);
        this.deps.requestFrame('clouds');
        return root;
    }

    /** Apply a partial update: transforms and look in place; shape inputs regenerate (debounced in the editor). */
    updateLayer(id: string, patch: Partial<CloudLayerData>): void {
        const layer = this.layers.get(id);
        if (!layer || !patch) return;
        const before = layer.look;
        layer.data = { ...layer.data, ...patch, id };
        if (patch.position || patch.rotation || patch.scale || 'area' in patch) this.applyTransform(layer, { ...patch, scale: patch.scale ?? layer.data.scale });
        if ('enabled' in patch) layer.root.enabled = patch.enabled !== false;
        layer.look = this.resolveLook(layer);
        if (cloudGeometryChanged(before, layer.look)) {
            this.syncScale(layer, true);
            const delay = this.deps.regenerateDelayMs ?? 0;
            if (delay > 0 && layer.geometry) layer.regenAt = performance.now() + delay;
            else this.generate(layer);
        }
        layer.pushed.clear();
        this.deps.requestFrame('clouds');
    }

    /**
     * The scene's entity animations. A layer whose size is keyframed generates once for the
     * largest size its keyframes reach; while the timeline plays, its live box is a window into
     * those clouds. Called at load and whenever the editor's clips change.
     */
    setAnimations(animations: ReadonlyArray<EntityAnimationConfig> | null | undefined): void {
        this.animations = Array.isArray(animations) ? animations : [];
        for (const layer of this.layers.values()) {
            const next = cloudScaleKeyframeExtent(this.animations, layer.id);
            if (JSON.stringify(next) === JSON.stringify(layer.keyframedScale)) continue;
            layer.keyframedScale = next;
            if (!layer.geometry || this.generationKey(layer) === layer.genKey) continue;
            const delay = this.deps.regenerateDelayMs ?? 0;
            if (delay > 0) layer.regenAt = performance.now() + delay;
            else this.generate(layer);
            this.deps.requestFrame('clouds');
        }
    }

    remove(id: string): void {
        const layer = this.layers.get(id);
        if (!layer) return;
        this.layers.delete(id);
        layer.root.destroy();
        layer.container?.destroy();
        layer.densityTex?.destroy();
        layer.shadowTex?.destroy();
        this.shadowLayers = this.shadowLayers.filter((l) => l !== layer);
        this.deps.requestFrame('clouds');
    }

    clear(): void {
        for (const id of [...this.layers.keys()]) this.remove(id);
    }

    destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        this.clear();
        this.deps.app.off('update', this.onUpdate);
        this.deps.app.off('prerender', this.onPrerender);
        // the shadow placeholder is shared per device (cloudShadowPlaceholder.ts): not ours to destroy
        this.shadowPlaceholder = null;
        this.scanGridPlaceholder?.destroy();
        this.scanGridPlaceholder = null;
        this.meshShadows?.destroy();
        this.meshShadows = null;
    }

    /** The scan's density grid: cloud splats inside solid scan content dissolve (clearance). */
    setScanGrid(binding: ScanGridBinding | null): void {
        if (binding === this.scanGrid || (binding && this.scanGrid && binding.texture === this.scanGrid.texture)) return;
        this.scanGrid = binding;
        for (const layer of this.layers.values()) layer.pushed.clear();
        this.deps.requestFrame('clouds-clearance');
    }

    /** Editor: outline a layer's box (or dome) while it is selected. */
    setBoundsVisible(id: string | null): void {
        for (const layer of this.layers.values()) layer.showBounds = layer.id === id;
        this.deps.requestFrame('clouds-bounds');
    }

    /** Diagnostics: per-layer counts, tier and timing. */
    debugInfo(): Record<string, unknown> {
        return {
            tier: this.tier,
            suspended: this.suspended,
            animatingOnScreen: this.animatingOnScreen,
            meshShadowMaterials: this.meshShadowMaterials,
            sun: this.sunEntityRef?.name ?? null,
            sunDir: [this.sunDir.x, this.sunDir.y, this.sunDir.z],
            layers: [...this.layers.values()].map((l) => ({
                id: l.id,
                preset: l.look.preset,
                area: l.look.area,
                seed: l.look.seed,
                coverage: l.look.coverage,
                windSpeed: l.look.windSpeed,
                windDirection: l.look.windDirection,
                enabled: l.root.enabled,
                splats: l.geometry?.count ?? 0,
                clouds: l.geometry?.clouds ?? 0,
                budget: cloudLayerBudget(l.look.quality, this.tier, this.layers.size),
                generateMs: Math.round(l.genMs),
                onScreen: l.onScreen,
                animating: l.animating,
                centerRefreshes: l.centerRefreshes,
                box: { ...l.box },
                genBox: l.geometry ? { ...l.geometry.genBox } : null,
                keyframedSize: l.keyframedScale ? { ...l.keyframedScale } : null,
                generations: l.genVersion,
                castsShadows: this.castsShadows(l),
            })),
        };
    }

    // ── animation (keyframes: cloud.coverage, cloud.color.r … colours in sRGB 0..1) ───────

    readAnimatable(entity: pc.Entity): Record<string, number> | null {
        const id = this.layerOfEntity(entity);
        const layer = id ? this.layers.get(id) : null;
        if (!layer) return null;
        const look = layer.look;
        const c = cloudHexToSrgb(look.color);
        const s = cloudHexToSrgb(look.shadeColor);
        return {
            'cloud.coverage': look.coverage,
            'cloud.density': look.density,
            'cloud.opacity': look.opacity,
            'cloud.windSpeed': look.windSpeed,
            'cloud.windDirection': look.windDirection,
            'cloud.evolution': look.evolution,
            'cloud.shadowStrength': look.shadowStrength,
            'cloud.color.r': c[0], 'cloud.color.g': c[1], 'cloud.color.b': c[2],
            'cloud.shadeColor.r': s[0], 'cloud.shadeColor.g': s[1], 'cloud.shadeColor.b': s[2],
        };
    }

    /** Returns true when anything changed. */
    writeAnimatable(entity: pc.Entity, values: Map<string, number>): boolean {
        const id = this.layerOfEntity(entity);
        const layer = id ? this.layers.get(id) : null;
        if (!layer) return false;
        const a = layer.animated;
        let touched = false;
        const scalar = (key: string, field: keyof CloudLayerData, lo: number, hi: number) => {
            if (!values.has(key)) return;
            const v = Math.min(hi, Math.max(lo, values.get(key)!));
            if ((a as Record<string, unknown>)[field] !== v) {
                (a as Record<string, unknown>)[field] = v;
                touched = true;
            }
        };
        scalar('cloud.coverage', 'coverage', 0, 1);
        scalar('cloud.density', 'density', 0, 1);
        scalar('cloud.opacity', 'opacity', 0, 1);
        scalar('cloud.windSpeed', 'windSpeed', 0, 1e6);
        scalar('cloud.windDirection', 'windDirection', -1e6, 1e6);
        scalar('cloud.evolution', 'evolution', 0, 1);
        scalar('cloud.shadowStrength', 'shadowStrength', 0, 1);
        const color = (prefix: string, field: 'color' | 'shadeColor') => {
            if (!values.has(`${prefix}.r`) && !values.has(`${prefix}.g`) && !values.has(`${prefix}.b`)) return;
            const cur = cloudHexToSrgb(layer.look[field]);
            const hex = cloudSrgbToHex([values.get(`${prefix}.r`) ?? cur[0], values.get(`${prefix}.g`) ?? cur[1], values.get(`${prefix}.b`) ?? cur[2]]);
            if (a[field] !== hex) {
                a[field] = hex;
                touched = true;
            }
        };
        color('cloud.color', 'color');
        color('cloud.shadeColor', 'shadeColor');
        if (touched) layer.look = this.resolveLook(layer);
        return touched;
    }

    resetAnimatable(entity: pc.Entity, properties: string[]): void {
        const id = this.layerOfEntity(entity);
        const layer = id ? this.layers.get(id) : null;
        if (!layer) return;
        const map: Record<string, keyof CloudLayerData> = {
            'cloud.coverage': 'coverage', 'cloud.density': 'density', 'cloud.opacity': 'opacity',
            'cloud.windSpeed': 'windSpeed', 'cloud.windDirection': 'windDirection', 'cloud.evolution': 'evolution',
            'cloud.shadowStrength': 'shadowStrength',
        };
        for (const p of properties) {
            if (map[p]) delete layer.animated[map[p]];
            if (p.startsWith('cloud.color')) delete layer.animated.color;
            if (p.startsWith('cloud.shadeColor')) delete layer.animated.shadeColor;
        }
        layer.look = this.resolveLook(layer);
    }

    // ── construction ─────────────────────────────────────────────────────────────────────

    /**
     * The layer's look: its data, keyframed values, and (while it follows the scene wind and the
     * scene has wind) the scene wind's direction and speed times its multiplier, which then win
     * over the layer's own wind fields.
     */
    private resolveLook(layer: CloudLayerRuntime): CloudLook {
        const wind = cloudSceneWind(layer.data, this.deps.sceneWind?.() ?? null);
        layer.sceneWindKey = wind ? `${wind.windDirection}|${wind.windSpeed}` : '';
        return resolveCloudLook({ ...layer.data, ...layer.animated, ...(wind ?? {}) });
    }

    private applyTransform(layer: CloudLayerRuntime, data: Partial<CloudLayerData>): void {
        const { toPlayCanvasPosition, finiteVec3 } = this.deps;
        if (data.position) {
            const p = toPlayCanvasPosition(data.position);
            layer.root.setLocalPosition(p.x, p.y, p.z);
        }
        if (data.rotation) {
            // Clouds stay level: only the yaw of the authored rotation (radians, editor convention) is used.
            const r = finiteVec3(data.rotation);
            layer.root.setLocalEulerAngles(0, (r.y * 180) / Math.PI, 0);
        }
        if (data.scale) {
            const s = finiteVec3(data.scale, { x: 100, y: 20, z: 100 });
            const dome = (data.area ?? layer.data.area) === 'dome';
            layer.root.setLocalScale(Math.max(0.01, Math.abs(s.x)), Math.max(0.01, Math.abs(s.y)), Math.max(0.01, Math.abs(dome ? s.x : s.z)));
        }
    }

    /** The child cancels the root's scale (splats are never stretched); the box size becomes a uniform. */
    private syncScale(layer: CloudLayerRuntime, force = false): boolean {
        const s = layer.root.getLocalScale();
        const key = `${s.x},${s.y},${s.z},${layer.look.area}`;
        if (key === layer.scaleKey && !force) return false;
        layer.scaleKey = key;
        const sx = Math.max(1e-4, Math.abs(s.x));
        const sy = Math.max(1e-4, Math.abs(s.y));
        const sz = Math.max(1e-4, Math.abs(s.z));
        layer.splat.setLocalScale(1 / sx, 1 / sy, 1 / sz);
        layer.box = { x: sx, y: sy, z: layer.look.area === 'dome' ? sx : sz };
        return true;
    }

    private budgetFor(layer: CloudLayerRuntime): number {
        return cloudLayerBudget(layer.look.quality, this.tier, this.layers.size);
    }

    /**
     * The box the layer's clouds are generated for. A keyframed size: the authored size grown to
     * the largest the keyframes reach (fixed while the timeline plays, so the editor and the
     * published viewer never regenerate mid-animation). Otherwise the live box.
     */
    private generationBox(layer: CloudLayerRuntime): { x: number; y: number; z: number } {
        if (!layer.keyframedScale) return layer.box;
        const s = this.deps.finiteVec3(layer.data.scale, { x: 100, y: 20, z: 100 });
        return cloudGenerationBox(s, layer.keyframedScale, layer.look.area === 'dome');
    }

    /**
     * What the geometry depends on. A keyframed size counts through its generation box (above).
     * Otherwise the box size counts only in the editor (a resized box refills evenly once the
     * drag settles; until then the box is a window into the clouds it had); the published viewer
     * has no other way to change it.
     */
    private generationKey(layer: CloudLayerRuntime): string {
        const l = layer.look;
        const b = this.generationBox(layer);
        const box = layer.keyframedScale || this.deps.regenerateDelayMs !== undefined || !layer.geometry ? [b.x.toFixed(2), b.y.toFixed(2), b.z.toFixed(2)] : layer.genKey.split('|').slice(5);
        return [l.preset, l.cloudScale, l.seed, l.area, this.budgetFor(layer), ...box].join('|');
    }

    private generate(layer: CloudLayerRuntime): void {
        layer.regenAt = 0;
        const key = this.generationKey(layer);
        if (key === layer.genKey && layer.geometry) return;
        layer.genKey = key;
        const t0 = performance.now();
        const geometry = generateCloudLayer({ look: layer.look, box: this.generationBox(layer), budget: this.budgetFor(layer) });
        layer.genMs = performance.now() - t0;
        layer.genVersion++;
        const device = this.deps.app.graphicsDevice;
        const format = cloudFormat(device);
        const count = geometry.count;
        let container = layer.container;
        if (!container || container.maxSplats < count) {
            // Keep the old resource on screen until the new one is attached (no flicker).
            const PC = pc as unknown as { GSplatContainer: new (d: pc.GraphicsDevice, n: number, f: pc.GSplatFormat) => pc.GSplatContainer };
            const old = container;
            container = new PC.GSplatContainer(device, Math.max(count, this.budgetFor(layer)), format);
            layer.container = container;
            if (old) this.deps.app.once('postrender', () => old.destroy());
        }
        const streams = [geometry.anchor, geometry.offset, geometry.rotation, geometry.shape, geometry.extra];
        CLOUD_STREAMS.forEach((name, i) => {
            const tex = (container as unknown as { getTexture(n: string): pc.Texture }).getTexture(name);
            const data = tex.lock() as Float32Array;
            data.fill(0);
            data.set(streams[i].subarray(0, Math.min(streams[i].length, data.length)));
            tex.unlock();
        });
        layer.geometry = geometry;
        this.refreshCenters(layer);
        this.updateAabb(layer);

        // extinction grid (RG8 2D array: one x-z slice per height level; 3D textures can't be
        // uploaded on WebGPU in PlayCanvas 2.21) and the cloud-shadow map (2D, RG8)
        const [gx, gy, gz] = geometry.gridDims;
        const slice = gx * gz * 2;
        const slices: Uint8Array[] = [];
        for (let y = 0; y < gy; y++) slices.push(geometry.grid.subarray(y * slice, (y + 1) * slice));
        layer.densityTex?.destroy();
        layer.densityTex = new pc.Texture(device, {
            name: `${layer.id}-cloudDensity`,
            width: gx,
            height: gz,
            arrayLength: gy,
            format: pc.PIXELFORMAT_RG8,
            mipmaps: false,
            minFilter: pc.FILTER_LINEAR,
            magFilter: pc.FILTER_LINEAR,
            addressU: pc.ADDRESS_REPEAT,
            addressV: pc.ADDRESS_REPEAT,
            levels: [slices] as unknown as Uint8Array[],
        });
        const [sx, sz] = geometry.shadowDims;
        layer.shadowTex?.destroy();
        layer.shadowTex = new pc.Texture(device, {
            name: `${layer.id}-cloudShadow`,
            width: sx,
            height: sz,
            format: pc.PIXELFORMAT_RG8,
            mipmaps: false,
            minFilter: pc.FILTER_LINEAR,
            magFilter: pc.FILTER_LINEAR,
            addressU: pc.ADDRESS_REPEAT,
            addressV: pc.ADDRESS_REPEAT,
            levels: [geometry.shadow],
        });

        if (!layer.splat.gsplat) {
            layer.splat.addComponent('gsplat', { resource: container, unified: true });
        } else if ((layer.splat.gsplat as unknown as { resource: unknown }).resource !== container) {
            (layer.splat.gsplat as unknown as { resource: unknown }).resource = container;
        }
        const gs = layer.splat.gsplat as unknown as { setParameter(n: string, v: unknown): void };
        gs.setParameter(CLOUD_UNIFORMS.density, layer.densityTex);
        layer.pushed.clear();
        this.pushUniforms(layer, true);
        this.deps.requestFrame('clouds-generate');
    }

    private updateAabb(layer: CloudLayerRuntime): void {
        const g = layer.geometry;
        const container = layer.container;
        if (!g || !container) return;
        const b = layer.box;
        // the clouds rest on the live box's floor; above a thinner live box they dissolve
        const lift = 0.5 * (g.genBox.y - b.y);
        const top = Math.min(g.extent.top + lift, b.y / 2) + g.extent.side * 0.1;
        const bottom = g.extent.bottom + lift - (layer.look.area === 'dome' ? this.curvature(layer) * (b.x * b.x) * 0.5 : 0);
        const half = new pc.Vec3(b.x / 2 + g.extent.side, (top - bottom) / 2, b.z / 2 + g.extent.side);
        (container as unknown as { aabb: pc.BoundingBox }).aabb = new pc.BoundingBox(new pc.Vec3(0, (top + bottom) / 2, 0), half);
    }

    private curvature(layer: CloudLayerRuntime): number {
        // the dome's edge sinks by 8% of its radius, toward the horizon
        const r = layer.box.x / 2;
        return layer.look.area === 'dome' && r > 0 ? 0.08 / r : 0;
    }

    /** Recompute the container's CPU centres (the CPU sorter's view of the clouds). */
    private refreshCenters(layer: CloudLayerRuntime): void {
        const g = layer.geometry;
        const container = layer.container;
        if (!g || !container) return;
        const centers = (container as unknown as { centers: Float32Array }).centers;
        const k = this.curvature(layer);
        for (let i = 0; i < g.count; i++) cloudSplatCenter(g, i, layer.box, layer.drift.u, layer.drift.v, k, centers, i * 3);
        (container as unknown as { update(n: number, c: boolean): void }).update(g.count, true);
        layer.driftSinceRefresh = 0;
        layer.refreshedAt = performance.now();
        layer.centerRefreshes++;
    }

    private castsShadows(layer: CloudLayerRuntime): boolean {
        return !!layer.geometry && this.shown(layer) && layer.look.castShadows && layer.look.shadowStrength > 0 && layer.look.opacity > 0 && layer.look.coverage > 0;
    }

    // ── per frame ────────────────────────────────────────────────────────────────────────

    private tick(dt: number): void {
        if (!this.layers.size) {
            this.animatingOnScreen = false;
            return;
        }
        const nextTier = selectCloudTier(this.deps.qualityInputs());
        if (nextTier !== this.tier) {
            this.tier = nextTier;
            for (const layer of this.layers.values()) layer.regenAt = performance.now();
        }
        if (++this.sunScan >= 10 || !this.sunEntityRef) {
            this.sunScan = 0;
            this.scanSun();
        } else if (this.sunEntityRef) {
            this.readSun(this.sunEntityRef);
        }
        const step = Math.min(Math.max(dt, 0), 0.25);
        const cam = this.deps.camera.camera;
        // the view frustum's corners in world space (for the on-screen test); not in XR, where the
        // headset's per-eye projections differ from the camera's own field of view and the frustum
        // planes (already widened to both eyes) are the only reliable test
        let corners: pc.Vec3[] | undefined;
        const xrActive = !!(this.deps.app as unknown as { xr?: { active?: boolean } }).xr?.active;
        if (cam && this.layers.size && !xrActive) {
            const local = cam.camera.getFrustumCorners();
            const m = this.deps.camera.getWorldTransform();
            for (let k = 0; k < 8; k++) m.transformPoint(local[k], _corners[k]);
            corners = _corners;
        }
        const now = performance.now();
        let animating = false;
        for (const layer of this.layers.values()) {
            // A layer following the scene wind picks up its changes (settings, keyframes).
            if (layer.data.followSceneWind || layer.sceneWindKey) {
                const wind = cloudSceneWind(layer.data, this.deps.sceneWind?.() ?? null);
                const windKey = wind ? `${wind.windDirection}|${wind.windSpeed}` : '';
                if (windKey !== layer.sceneWindKey) {
                    layer.look = this.resolveLook(layer);
                    layer.pushed.clear();
                }
            }
            if (this.syncScale(layer)) {
                this.updateAabb(layer);
                if (layer.geometry) this.refreshCenters(layer);
                layer.pushed.clear();
                // a new box size settles into a fresh generation (clouds refill the box evenly)
                if (layer.geometry && this.generationKey(layer) !== layer.genKey) layer.regenAt = now + (this.deps.regenerateDelayMs ?? 0) + 250;
            } else if (layer.geometry && layer.regenAt === 0 && this.generationKey(layer) !== layer.genKey) {
                // the tier or the number of layers changed the budget
                layer.regenAt = now + (this.deps.regenerateDelayMs ?? 0);
            }
            if (layer.regenAt > 0 && now >= layer.regenAt) this.generate(layer);
            const look = layer.look;
            const moving = look.windSpeed > 0 || look.evolution > 0 || look.lifecycle;
            layer.animating = moving && this.shown(layer) && look.opacity > 0;
            if (moving) {
                layer.time = (layer.time + step) % CLOUD_TIME_PERIOD;
                const w = windVector(look.windDirection);
                // scene XZ → PlayCanvas (z mirrored) → the layer's frame (undo its yaw)
                _v.set(w.x, 0, -w.z);
                _q.copy(layer.root.getLocalRotation()).invert();
                _q.transformVector(_v, _v2);
                const dist = look.windSpeed * step;
                // drift wraps through the generated layer (the live box is a window into it)
                const gb = layer.geometry?.genBox ?? layer.box;
                layer.drift.u = wrap01(layer.drift.u + (_v2.x * dist) / gb.x);
                layer.drift.v = wrap01(layer.drift.v + (_v2.z * dist) / gb.z);
                layer.driftSinceRefresh += dist;
            }
            layer.onScreen = !!cam && this.shown(layer) && !!layer.container && boxInFrustum(this.worldAabb(layer), cam.frustum, corners);
            if (layer.onScreen && layer.animating) animating = true;
        }
        this.animatingOnScreen = animating;
    }

    private worldAabb(layer: CloudLayerRuntime): pc.BoundingBox {
        const out = new pc.BoundingBox();
        const local = (layer.container as unknown as { aabb: pc.BoundingBox } | null)?.aabb;
        if (!local) return out;
        out.setFromTransformedAabb(local, layer.splat.getWorldTransform());
        return out;
    }

    private prerender(): void {
        if (!this.layers.size) return;
        const usesCpuSort = (this.deps.app.scene as unknown as { gsplat?: { currentRenderer?: number } }).gsplat?.currentRenderer === ((pc as unknown as { GSPLAT_RENDERER_RASTER_CPU_SORT?: number }).GSPLAT_RENDERER_RASTER_CPU_SORT ?? 1);
        const now = performance.now();
        for (const layer of this.layers.values()) {
            if (!layer.geometry || !this.shown(layer)) continue;
            if (layer.showBounds) this.drawBounds(layer);
            if (!layer.onScreen && layer.pushed.size > 0) continue;
            // the CPU sorter needs current centres once the clouds have drifted noticeably
            if (usesCpuSort && layer.driftSinceRefresh > Math.max(layer.look.cloudScale * 0.04, 1e-3) && now - layer.refreshedAt > 250) {
                this.refreshCenters(layer);
            }
            this.pushUniforms(layer, false);
        }
    }

    /** Push the layer's uniforms; only values that changed are set (each set re-copies its splats). */
    private pushUniforms(layer: CloudLayerRuntime, force: boolean): void {
        const g = layer.geometry;
        const gs = layer.splat.gsplat as unknown as { setParameter(n: string, v: unknown): void } | undefined;
        if (!g || !gs) return;
        const look = layer.look;
        const relight = this.deps.relighting();
        // with relighting on, the relighting shader applies the light's colour; the clouds keep neutral light
        const fade = relight.enabled ? Math.min(1, Math.max(0, relight.fade)) : 0;
        // clouds scatter many times: they take the sun's tint, less saturated than the light itself
        const lum = 0.2126 * this.sunColor[0] + 0.7152 * this.sunColor[1] + 0.0722 * this.sunColor[2];
        const sunColor = this.sunColor.map((c) => {
            const tinted = lum + (c - lum) * 0.6;
            return tinted + (1 - tinted) * fade;
        }) as [number, number, number];
        // toward the sun, in the layer's frame
        _m.copy(layer.splat.getWorldTransform()).invert();
        _m.transformVector(this.sunDir, _v).normalize();
        const sunUp = this.sunDir.y;
        const sunVis = Math.min(1, Math.max(0, (sunUp + 0.04) / 0.14));
        // camera in the layer's frame
        _m.transformPoint(this.deps.camera.getPosition(), _v2);
        const albedo = cloudHexToLinear(look.color);
        const shade = cloudHexToLinear(look.shadeColor);
        const sky = this.skyLight();
        const fadeWidth = Math.min(0.24, Math.max(0.03, (look.cloudScale * 0.6) / Math.max(1e-3, Math.min(layer.box.x, layer.box.z))));
        const set = (name: string, values: number[]) => {
            const prev = layer.pushed.get(name);
            // compare as float32, as stored and uploaded: 0.3 !== Float32Array.of(0.3)[0], which
            // re-pushed (and re-copied, and on WebGL2 re-sorted) on screen layers every frame
            if (!force && prev && prev.length === values.length && prev.every((v, i) => v === Math.fround(values[i]))) return;
            const arr = new Float32Array(values);
            layer.pushed.set(name, arr);
            gs.setParameter(name, arr);
        };
        set(CLOUD_UNIFORMS.box, [layer.box.x, layer.box.y, layer.box.z, look.area === 'dome' ? 1 : 0]);
        set(CLOUD_UNIFORMS.genBox, [g.genBox.x, g.genBox.y, g.genBox.z, this.curvature(layer)]);
        set(CLOUD_UNIFORMS.grid, [g.gridY[0], g.gridY[1], g.sigmaMax, look.coverage]);
        set(CLOUD_UNIFORMS.wind, [layer.drift.u, layer.drift.v, layer.time, look.evolution]);
        set(CLOUD_UNIFORMS.shape, [look.density * look.opacity, look.softness, fadeWidth, look.lifecycle ? 1 : 0]);
        set(CLOUD_UNIFORMS.sun, [_v.x, _v.y, _v.z, sunVis]);
        set(CLOUD_UNIFORMS.sunColor, [...sunColor, look.phaseG]);
        set(CLOUD_UNIFORMS.sky, [sky[0], sky[1], sky[2], look.ambient]);
        set(CLOUD_UNIFORMS.ground, [0.26, 0.25, 0.23, look.silver]);
        set(CLOUD_UNIFORMS.albedo, [...albedo, look.powder]);
        set(CLOUD_UNIFORMS.shade, [...shade, look.stylised ? 1 : 0]);
        // the camera only steers the view-dependent light: coarse steps avoid re-copying for tiny moves
        const q = Math.max(1e-3, look.cloudScale * 0.004);
        set(CLOUD_UNIFORMS.camera, [quantize(_v2.x, q), quantize(_v2.y, q), quantize(_v2.z, q), 1]);
        set(CLOUD_UNIFORMS.look, [Math.max(1e-3, look.cloudScale * 0.32), g.cellSize, DIRECT_GAIN, CLOUD_LIFE_PERIOD]);
        set(CLOUD_UNIFORMS.gridSize, [g.gridDims[0], g.gridDims[1], g.gridDims[2], 0]);
        // clearance against the scan
        set(CLOUD_UNIFORMS.toWorld, Array.from(layer.splat.getWorldTransform().data));
        const sg = this.scanGrid;
        set(CLOUD_UNIFORMS.scanGrid, sg ? [sg.origin[0], sg.origin[1], sg.origin[2], sg.cell] : [0, 0, 0, 1]);
        set(CLOUD_UNIFORMS.scanGridSize, sg ? [sg.dims[0], sg.dims[1], sg.dims[2], 1] : [1, 1, 1, 0]);
        // fog may touch the ground (its own cell only, partly); clouds keep a cell clear of the scan
        const fog = look.shape.kind === 'fog';
        set(CLOUD_UNIFORMS.extra, [fog ? 0 : 1, fog ? 0.5 : 1, look.shape.kind === 'cirrus' || fog ? 0 : 0.7, fog ? 1 : 0]);
        const scanTex = sg?.texture ?? this.gridPlaceholder();
        if (layer.pushed.get('__scanTex') !== (scanTex as unknown as Float32Array)) {
            layer.pushed.set('__scanTex', scanTex as unknown as Float32Array);
            gs.setParameter(CLOUD_UNIFORMS.scanDensity, scanTex);
        }
    }

    /** Daylight sky light from above, nudged toward the scene's background colour. */
    private skyLight(): [number, number, number] {
        const clear = this.deps.camera.camera?.clearColor;
        const base: [number, number, number] = [0.56, 0.68, 0.92];
        if (!clear) return base;
        const lin = [clear.r, clear.g, clear.b].map((c) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
        return base.map((b, i) => b * 0.8 + lin[i] * 0.2) as [number, number, number];
    }

    /** The scene's sun: the brightest enabled directional light shining down. */
    private scanSun(): void {
        const best = pickCloudSun(this.deps.app.root.findComponents('light') as pc.LightComponent[]);
        this.sunEntityRef = best?.entity ?? null;
        if (best) this.readSun(best.entity);
        else {
            this.sunDir.copy(FALLBACK_SUN);
            this.sunColor = [1, 0.97, 0.92];
        }
    }

    private readSun(entity: pc.Entity): void {
        const light = entity.light;
        if (!light || !entity.enabled || !light.enabled) {
            this.sunEntityRef = null;
            return;
        }
        // directional lights shine down their -Y axis: +Y points at the sun
        this.sunDir.copy(entity.up).normalize();
        const c = light.color;
        const k = Math.min(Math.max(light.intensity, 0), 3);
        const lin = [c.r, c.g, c.b].map((v) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
        this.sunColor = [lin[0] * k, lin[1] * k, lin[2] * k];
    }

    private drawBounds(layer: CloudLayerRuntime): void {
        const app = this.deps.app as pc.AppBase & { drawLine(a: pc.Vec3, b: pc.Vec3, c: pc.Color, depth?: boolean): void };
        const m = layer.splat.getWorldTransform();
        const b = layer.box;
        const color = new pc.Color(0.55, 0.8, 1);
        const pt = (x: number, y: number, z: number) => m.transformPoint(new pc.Vec3(x, y, z));
        if (layer.look.area === 'dome') {
            const r = b.x / 2;
            const k = this.curvature(layer);
            for (const y of [-b.y / 2, b.y / 2]) {
                for (let i = 0; i < 48; i++) {
                    const a0 = (i / 48) * Math.PI * 2;
                    const a1 = ((i + 1) / 48) * Math.PI * 2;
                    app.drawLine(pt(Math.cos(a0) * r, y - k * r * r, Math.sin(a0) * r), pt(Math.cos(a1) * r, y - k * r * r, Math.sin(a1) * r), color, false);
                }
            }
            return;
        }
        const hx = b.x / 2, hy = b.y / 2, hz = b.z / 2;
        const c = [
            pt(-hx, -hy, -hz), pt(hx, -hy, -hz), pt(hx, -hy, hz), pt(-hx, -hy, hz),
            pt(-hx, hy, -hz), pt(hx, hy, -hz), pt(hx, hy, hz), pt(-hx, hy, hz),
        ];
        const edges = [[0, 1], [1, 2], [2, 3], [3, 0], [4, 5], [5, 6], [6, 7], [7, 4], [0, 4], [1, 5], [2, 6], [3, 7]];
        for (const [i, j] of edges) app.drawLine(c[i], c[j], color, false);
    }

    // ── cloud shadows on the scan ────────────────────────────────────────────────────────

    /**
     * Bind the cloud-shadow uniforms to the splat receiver materials (the relighting shader).
     * `relightingOn`: the shadow removes the sun's light instead of darkening the colour.
     */
    bindShadowReceivers(materials: ReadonlyArray<pc.Material>, relightingOn: boolean): void {
        const device = this.deps.app.graphicsDevice;
        const casting = [...this.layers.values()].filter((l) => this.castsShadows(l)).slice(0, MAX_CLOUD_SHADOW_LAYERS);
        this.shadowLayers = casting;
        const params = new Float32Array(4 * MAX_CLOUD_SHADOW_LAYERS);
        const light = new Float32Array(4 * MAX_CLOUD_SHADOW_LAYERS);
        const wind = new Float32Array(4 * MAX_CLOUD_SHADOW_LAYERS);
        const box = new Float32Array(4 * MAX_CLOUD_SHADOW_LAYERS);
        const matrices = new Float32Array(16 * MAX_CLOUD_SHADOW_LAYERS);
        const maps: pc.Texture[] = [];
        const mapKeys: string[] = [];
        casting.forEach((layer, slot) => {
            const g = layer.geometry!;
            const look = layer.look;
            const inv = new pc.Mat4().copy(layer.splat.getWorldTransform()).invert();
            const L = inv.transformVector(this.sunDir, new pc.Vec3()).normalize();
            const lift = 0.5 * (g.genBox.y - layer.box.y);
            const bottom = (g.extent.bottom + lift) * 0.6 + (-layer.box.y / 2) * 0.4;
            const mid = (g.gridY[0] + g.gridY[1]) / 2 + lift;
            // a live box thinner than the generated one hides the tops: lighter shadows
            const visibleDepth = Math.min(1, layer.box.y / Math.max(1e-6, g.genBox.y));
            const sunVis = Math.min(1, Math.max(0, (this.sunDir.y - 0.02) / 0.15));
            params.set([look.shadowStrength * look.opacity * Math.min(1, look.density * 1.25) * sunVis, g.tauMax * 0.9 * visibleDepth, mid, bottom], slot * 4);
            light.set([L.x, L.y, L.z, look.coverage], slot * 4);
            const fw = Math.min(0.24, Math.max(0.03, (look.cloudScale * 0.6) / Math.max(1e-3, Math.min(layer.box.x, layer.box.z))));
            // With the clouds on screen their shadows follow every frame. Off screen (looking at the
            // ground) the soft shadows step only once they've moved 1% of a cloud's size, so a
            // scene with drifting clouds overhead redraws a few times a second, not every frame.
            const du = wrapSigned(layer.drift.u - layer.shadowDrift.u) * g.genBox.x;
            const dv = wrapSigned(layer.drift.v - layer.shadowDrift.v) * g.genBox.z;
            if (layer.onScreen || Math.hypot(du, dv) >= look.cloudScale * 0.01) {
                layer.shadowDrift.u = layer.drift.u;
                layer.shadowDrift.v = layer.drift.v;
            }
            wind.set([layer.shadowDrift.u, layer.shadowDrift.v, look.area === 'dome' ? 1 : 0, fw], slot * 4);
            // generated x, live x, generated z, live z (the live box is a window, as in the read code)
            box.set([g.genBox.x, layer.box.x, g.genBox.z, layer.box.z], slot * 4);
            matrices.set(inv.data, slot * 16);
            maps.push(layer.shadowTex!);
            mapKeys.push(`${layer.id}#${layer.genVersion}`);
        });
        const placeholder = this.placeholder(device);
        while (maps.length < MAX_CLOUD_SHADOW_LAYERS) maps.push(placeholder);
        const global = new Float32Array([casting.length > 0 ? 1 : 0, relightingOn ? 1 : 0, 0, 0]);
        const key = `${Array.from(params).join(',')}|${Array.from(light).join(',')}|${Array.from(wind).join(',')}|${Array.from(box).join(',')}|${Array.from(matrices).join(',')}|${global[0]},${global[1]}|${mapKeys.join(',')}`;
        let changed = false;
        for (const material of materials) {
            if (this.shadowKeys.get(material) === key) continue;
            this.shadowKeys.set(material, key);
            material.setParameter(CLOUD_SHADOW_UNIFORMS.params, params);
            material.setParameter(CLOUD_SHADOW_UNIFORMS.light, light);
            material.setParameter(CLOUD_SHADOW_UNIFORMS.wind, wind);
            material.setParameter(CLOUD_SHADOW_UNIFORMS.box, box);
            material.setParameter(CLOUD_SHADOW_UNIFORMS.matrix, matrices);
            material.setParameter(CLOUD_SHADOW_UNIFORMS.global, global);
            CLOUD_SHADOW_UNIFORMS.maps.forEach((name, i) => material.setParameter(name, maps[i]));
            material.update();
            changed = true;
        }
        if (changed) this.deps.requestFrame('cloud-shadows');
        this.bindMeshShadows(casting.length > 0, key, { params, light, wind, box, matrices, global, maps });
    }

    /**
     * The same shadows on models, the ground plane and the water: global-scope uniforms (bound on
     * change) and patched lit materials, only while some layer casts shadows.
     */
    private bindMeshShadows(on: boolean, key: string, values: { params: Float32Array; light: Float32Array; wind: Float32Array; box: Float32Array; matrices: Float32Array; global: Float32Array; maps: pc.Texture[] }): void {
        if (!on && !this.meshShadows) return;
        this.meshShadows ??= new CloudMeshShadows(this.deps.app.graphicsDevice);
        if (on) {
            const sunKey = `${key}|${this.sunDir.x.toFixed(4)},${this.sunDir.y.toFixed(4)},${this.sunDir.z.toFixed(4)}`;
            if (sunKey !== this.meshShadowKey) {
                this.meshShadowKey = sunKey;
                this.meshShadows.bind({ ...values, sunTravel: [-this.sunDir.x, -this.sunDir.y, -this.sunDir.z] });
                this.deps.requestFrame('cloud-shadows');
            }
        } else {
            this.meshShadowKey = '';
        }
        // bound first, then patched: a patched shader never runs without its uniforms
        this.meshShadows.sync(on, this.deps.meshReceivers ?? (() => []), performance.now());
        if (on !== this.meshShadowsOn) {
            this.meshShadowsOn = on;
            this.deps.onMeshShadows?.(on);
        }
    }

    /** Materials with cloud shadows patched in (diagnostics). */
    get meshShadowMaterials(): number {
        return this.meshShadows?.patchedCount ?? 0;
    }

    private gridPlaceholder(): pc.Texture {
        if (!this.scanGridPlaceholder) {
            this.scanGridPlaceholder = new pc.Texture(this.deps.app.graphicsDevice, {
                name: 'CloudScanGridPlaceholder',
                width: 1,
                height: 1,
                arrayLength: 1,
                format: pc.PIXELFORMAT_RG8,
                mipmaps: false,
                levels: [[new Uint8Array([0, 0])]] as unknown as Uint8Array[],
            });
        }
        return this.scanGridPlaceholder;
    }

    private placeholder(device: pc.GraphicsDevice): pc.Texture {
        if (!this.shadowPlaceholder) this.shadowPlaceholder = getCloudShadowPlaceholder(device);
        return this.shadowPlaceholder;
    }
}

function wrap01(x: number): number {
    return x - Math.floor(x);
}

function quantize(x: number, step: number): number {
    return Math.round(x / step) * step;
}
